import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Tabs, Divider, notification } from "antd";
import { ITripDetails } from "../../ServiceUsers/Interfaces/ITripDetails";
import styles from "./TripDetailsDrawer.module.scss";
import textStyles from "../../Shared/Styles/TextStyles.module.scss";
import dividerStyles from "../../Shared/Styles/DividerStyles.module.scss";
import TripLegCard from "../../Shared/Cards/TripCards/TripLegCard";
import GrandTotal from "../BookingSummary/GrandTotal";
import TripPaymentCard from "../../Shared/Cards/TripCards/TripPaymentCard";
import TripLegCostCard from "../../Shared/Cards/TripCards/TripLegCostCard";
import { getPaymentInstrumentByPaymentInstrumentId } from "../../../services/PaymentService/PaymentInstrumentService";
import {
  CreditCardDetails,
  defaultCreditCardDetails,
} from "../../../types/PaymentTypes";
import Spinner from "../../../assets/Loader/GoogleSvgLoader/GoogleLoader";
import moment from "moment";
import calendarIcon from "../../../assets/icons/calendarIcon.svg";
import { useTripsCtx } from "../../../context/TripDetailsContext";

type Props = {
  selectedLeg: ITripDetails;
  activeTripDetailsTab: string;
  handleTabChange: (activeKey: string) => void;
};

function UpcomingAndPastTripDetails({
  selectedLeg,
  handleTabChange,
  activeTripDetailsTab,
}: Props) {
  const [creditCardDetails, setCreditCardDetails] = useState<CreditCardDetails>(
    defaultCreditCardDetails
  );
  const [loading, setIsLoading] = useState<boolean>(false);

  const { upcomingTripDetailsCtx, pastTripDetailsCtx } = useTripsCtx();

  const [upcomingTripDetails] = upcomingTripDetailsCtx;
  const [pastTripDetails] = pastTripDetailsCtx;

  const trip = useMemo(() => {
    return [...upcomingTripDetails, ...pastTripDetails].filter(
      (leg: ITripDetails) =>
        leg.requestAssignmentId === selectedLeg.requestAssignmentId
    );
  }, [selectedLeg]);

  async function getCreditCardDetailsForTrip() {
    try {
      setIsLoading(true);
      const ccInfo = await getPaymentInstrumentByPaymentInstrumentId(
        trip[0].paymentInstrumentId
      );
      setCreditCardDetails(ccInfo);
    } catch (error) {
      notification.error({
        message: "Failed to Fetch Credit Card Details",
      });
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    activeTripDetailsTab === "2" && getCreditCardDetailsForTrip();
  }, [activeTripDetailsTab]);

  const { TabPane } = Tabs;

  return (
    <Tabs
      className={styles.tabsContainer}
      onChange={handleTabChange}
      activeKey={activeTripDetailsTab}
    >
      <TabPane tab="Trip Summary" key="1">
        <Row justify="space-between" gutter={[0, 24]}>
          <Col className={textStyles.normalText20}>Service date</Col>
          <Col>
            <span className={styles.tripDate}>
              <img src={calendarIcon} alt="trip_Date" />
              {moment(trip[0]?.tripDate).format("MM/DD/YYYY")}
            </span>
          </Col>
          <Col span={24} />
        </Row>

        {trip.map((leg: ITripDetails, index: number) => (
          <React.Fragment key={leg.bookingId}>
            <TripLegCard
              leg={leg}
              legNumber={index + 1}
              isMobileView={true}
              showStatus={true}
            />
            {trip.length > index + 1 && (
              <Divider className={dividerStyles.dashedPrimaryDivider} dashed />
            )}
          </React.Fragment>
        ))}
      </TabPane>

      <TabPane tab="Order" key="2">
        {loading ? (
          <Spinner />
        ) : (
          <>
            <TripPaymentCard trip={trip} ccInfo={creditCardDetails} />

            <Divider className={dividerStyles.dashedPrimaryDivider} dashed />

            {trip.map((leg: ITripDetails, index: number) => (
              <TripLegCostCard
                key={leg.bookingId}
                leg={leg}
                legNumber={index + 1}
                isWandaPlusTrip={trip[0]?.wandaSubscriptionPlan === "Plus"}
                hasPromoCode={!!trip[0]?.promoCodeDetail?.promoCode}
              />
            ))}

            <GrandTotal trip={trip} />
          </>
        )}
      </TabPane>
    </Tabs>
  );
}

export default UpcomingAndPastTripDetails;
